/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

/**
 * @module bulb-progress/bulb-upload/bulbretryfailedupload.js
 */
import $ from 'jquery';

import { Plugin, type Editor, type ModelWriter } from 'ckeditor5';

/**
 * Plugin that allows the user to retry a failed upload. It uses our angular upload-asset service.
 */
export default class BulbRetryFailedUpload extends Plugin {

  FILTER: any;

  /**
   * @inheritDoc
   */
  constructor(editor: Editor) {
    super(editor);

    /**
     * Filter for the allowed asset types.
     *
     * @type {{IMAGE: string, VIDEO: string, ATTACHED_FILE: string, AUDIO: string}}
     */
    this.FILTER = (window as any).bulbVars.ASSET_LIBRARY.FILTER;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbRetryFailedUpload' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { model } = editor;
    const fileFilters = this.FILTER;
    const createInputElementFunctionReference = this.createInputFileElement;

    window.addEventListener('retryFailedUploadBlock', () => {
      const selectedBlock = editor.model.document.selection.getSelectedElement();
      const assetType = selectedBlock.getAttribute('asset-type') as any;

      let allowedTypes = '*';
      if (assetType.includes('IMAGE')) {
        allowedTypes = fileFilters.IMAGE;
      } else if (assetType.includes('AUDIO')) {
        allowedTypes = fileFilters.AUDIO;
      } else if (assetType.includes('VIDEO')) {
        allowedTypes = fileFilters.VIDEO;
      }

      const $file = $(createInputElementFunctionReference(false));
      $file.attr('accept', allowedTypes);
      $file.addClass('hiddenUploader');
      $('body').append($file);

      // Retry Direct to S3 Upload, allowing the user to select the asset again:
      CKEBulbData.service.uploadAsset.uploadFile(
        $file,
        allowedTypes,
        false,
        'page',
      );

      // now remove failed upload block:
      model.change((writer: ModelWriter) => {
        writer.remove(selectedBlock);
      });
    });
  }

  /**
   * Helper method to create an input field for file upload.
   *
   * @param multiple If the user initiated a multi-part upload. ( We don't really support that... yet! ).
   * @returns {string} Input field for file upload.
   *
   * @private
   */
  private createInputFileElement(multiple): string {
    return multiple ? '<input type="file" multiple>' : '<input type="file">';
  }
}
